// OpenWarrantyClaimModal — staff path for opening a new warranty claim.
//
// Captures: title, category (workmanship/manufacturer/storm_damage/other),
// priority, reported_issue, and optional photos + files. Attachments go
// through lib/warrantyAttachments: deal-documents/warranty/{deal_id}/{claim_id}/,
// a deal_documents row per file (file_type warranty_photo | warranty_document,
// warranty_claim_id set) and a photo_added timeline event.
//
// On submit: insert warranty_claims row (DB triggers auto-default Jennifer as
// assignee + log 'opened' event) → upload attachments → notify (fire-and-
// forget) → close. A failed attachment never rolls the claim back — the modal
// names the files that didn't make it so they can be re-added from the
// Warranty tab (Sep 15, 2026: failures used to be swallowed with console.warn).

import { useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { notifyWarrantyClaimOpened, notifyWarrantyPhotoAdded } from "../../lib/notifyOnWarrantyClaim";
import { fmtSupabaseError } from "../../lib/fmtError";
import {
  describeAttachmentCounts,
  describeFailures,
  fmtBytes,
  uploadStaffWarrantyAttachments,
} from "../../lib/warrantyAttachments";
import AttachmentPicker from "../shared/AttachmentPicker";

interface DealForOpen {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  property_city: string | null;
  owner_id: string | null;
}

interface Props {
  deal: DealForOpen;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}

const CATEGORIES = [
  { value: "workmanship", label: "Workmanship (install issue)" },
  { value: "manufacturer", label: "Manufacturer (material defect)" },
  { value: "storm_damage", label: "Storm damage" },
  { value: "other", label: "Other" },
];

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
];

function fileKey(f: File): string {
  return `${f.name}|${f.size}|${f.lastModified}`;
}

export default function OpenWarrantyClaimModal({ deal, onClose, onCreated }: Props) {
  const { member } = useAuth();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<string>("workmanship");
  const [priority, setPriority] = useState<string>("normal");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Set when the claim was created but some attachments failed — the modal
  // stays open to show which ones, with a Done button instead of auto-closing.
  const [createdWarning, setCreatedWarning] = useState<string | null>(null);

  function addFiles(picked: File[]) {
    setFiles((prev) => {
      const seen = new Set(prev.map(fileKey));
      const next = [...prev];
      for (const f of picked) {
        const k = fileKey(f);
        if (!seen.has(k)) { seen.add(k); next.push(f); }
      }
      return next;
    });
  }

  function removeFile(key: string) {
    setFiles((prev) => prev.filter((f) => fileKey(f) !== key));
  }

  async function handleSubmit() {
    if (!title.trim()) {
      setErr("Title is required.");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      // 1) Insert claim. DB trigger auto-assigns Jennifer + logs 'opened' event.
      const { data: claim, error: insertErr } = await supabase
        .from("warranty_claims")
        .insert({
          deal_id: deal.id,
          claim_date: new Date().toISOString().slice(0, 10),
          reported_issue: description || title,
          title,
          category,
          priority,
          opened_via: "staff_manual",
          opened_by: member?.id ?? null,
          status: "claim_filed",
        })
        .select("id, title, category, status, opened_via")
        .single();
      if (insertErr || !claim) throw insertErr ?? new Error("Insert returned no row");

      const dealForNotify = {
        id: deal.id,
        homeowner_name: deal.homeowner_name,
        property_street: deal.property_street,
        property_city: deal.property_city,
        owner_id: deal.owner_id,
      };
      const claimForNotify = {
        id: claim.id,
        title: claim.title,
        category: claim.category,
        status: claim.status,
        opened_via: claim.opened_via,
      };
      notifyWarrantyClaimOpened(dealForNotify, claimForNotify);

      // 2) Attachments — each file independent; failures reported, never hidden.
      if (files.length > 0) {
        setProgress({ done: 0, total: files.length });
        const result = await uploadStaffWarrantyAttachments({
          dealId: deal.id,
          claimId: claim.id,
          files,
          uploaderId: member?.id ?? null,
          uploaderLabel: member?.full_name ?? null,
          note: `Uploaded with claim "${title}"`,
          onProgress: (done, total) => setProgress({ done, total }),
        });
        if (result.uploaded.length > 0) {
          notifyWarrantyPhotoAdded(
            dealForNotify,
            claimForNotify,
            result.uploaded.length,
            describeAttachmentCounts(result.photoCount, result.documentCount),
          );
        }
        if (result.failures.length > 0) {
          const n = result.failures.length;
          setCreatedWarning(
            `The claim was opened${result.uploaded.length > 0 ? ` with ${describeAttachmentCounts(result.photoCount, result.documentCount)}` : ""}, ` +
            `but ${n === 1 ? "1 file" : `${n} files`} couldn't be attached: ${describeFailures(result.failures)}. ` +
            "You can add them again from the Warranty tab.",
          );
          return;
        }
      }

      await onCreated();
    } catch (e) {
      setErr(fmtSupabaseError(e));
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-bardin-ink">Open warranty claim</h2>
          <button onClick={onClose} disabled={submitting} className="text-slate-400 hover:text-slate-600 disabled:opacity-50">✕</button>
        </div>

        {createdWarning ? (
          <div className="px-4 py-4 space-y-3">
            <div className="text-sm text-slate-700">✅ Claim opened.</div>
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">{createdWarning}</div>
            <div className="flex justify-end">
              <button onClick={() => { void onCreated(); }} className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90">
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-4 py-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-700">Title *</label>
                <input
                  type="text"
                  className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300 focus:border-bardin-teal focus:outline-none"
                  placeholder="e.g. Leak over kitchen"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={120}
                  disabled={submitting}
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-slate-700">Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    disabled={submitting}
                    className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300 focus:border-bardin-teal focus:outline-none"
                  >
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Priority</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    disabled={submitting}
                    className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300 focus:border-bardin-teal focus:outline-none"
                  >
                    {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700">Description</label>
                <textarea
                  className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300 focus:border-bardin-teal focus:outline-none min-h-[80px]"
                  placeholder="What did the homeowner report? When did it start?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700">Photos &amp; files (optional)</label>
                <div className="mt-1">
                  <AttachmentPicker onFiles={addFiles} disabled={submitting} />
                </div>
                {files.length > 0 && (
                  <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded">
                    {files.map((f) => {
                      const k = fileKey(f);
                      return (
                        <li key={k} className="flex items-center gap-2 px-2.5 py-1.5 text-xs">
                          <span className="flex-1 min-w-0 truncate text-slate-700">{f.name}</span>
                          <span className="text-slate-400 flex-shrink-0">{fmtBytes(f.size)}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(k)}
                            disabled={submitting}
                            className="text-slate-400 hover:text-red-600 disabled:opacity-50 px-1"
                            aria-label={`Remove ${f.name}`}
                          >
                            ✕
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {submitting && progress && (
                  <div className="mt-1 text-xs text-slate-500">
                    Uploading {Math.min(progress.done + 1, progress.total)} of {progress.total}…
                  </div>
                )}
              </div>

              {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">{err}</div>}

              <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Jennifer Ayers will be assigned and notified. Robert + the original RM also get a heads-up.
              </div>
            </div>

            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button onClick={onClose} disabled={submitting} className="px-3 py-1.5 text-xs font-medium rounded bg-slate-200 text-slate-700 hover:bg-slate-300 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={submitting || !title.trim()} className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 disabled:opacity-50">
                {submitting ? "Opening…" : "Open claim"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
